import { useRef, type MouseEvent } from 'react';
import { Crosshair } from 'lucide-react';

export interface FocalPoint {
  x: number;
  y: number;
}

/**
 * Selector de punto focal: el usuario hace clic sobre la imagen para marcar el
 * punto que debe permanecer visible al recortar (art direction). Devuelve
 * coordenadas normalizadas 0..1 que la web usa como `object-position`.
 */
export function FocalPointPicker({
  src,
  value,
  onChange,
}: {
  src: string;
  value: FocalPoint;
  onChange: (point: FocalPoint) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    onChange({ x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 });
  }

  return (
    <div className="space-y-1.5">
      <div
        ref={ref}
        onClick={handleClick}
        className="relative cursor-crosshair overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
        title="Haz clic para fijar el punto focal"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt="" className="max-h-64 w-full select-none object-contain" draggable={false} />
        <div
          className="pointer-events-none absolute -ml-3 -mt-3 grid size-6 place-items-center rounded-full border-2 border-white bg-[#AD026A]/80 text-white shadow-md ring-1 ring-black/20"
          style={{ left: `${value.x * 100}%`, top: `${value.y * 100}%` }}
        >
          <Crosshair className="size-3.5" />
        </div>
      </div>
      <p className="text-xs text-neutral-500">
        Punto focal: {Math.round(value.x * 100)}% / {Math.round(value.y * 100)}% · clic en la imagen para cambiarlo.
      </p>
    </div>
  );
}
